/**
 * AddCardDetailsStep - Details phase of the Add to Collection / Edit Card modals
 *
 * Big card image (with DFC flip), owned instances with +1, and the instance form.
 */
import clsx from 'clsx';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { IoBookmark, IoBookmarkOutline } from 'react-icons/io5';
import { TbRotate } from 'react-icons/tb';

import CardImage from '@/components/CardImage/CardImage';
import { useCollectionQuery } from '@/features/collection/hooks/api';
import useCardLookup from '@/hooks/useCardLookup';
import { getPrintingFinishes } from '@/lib/cardUtils';
import type { MinimalCard } from '@/services/cardDatabase';
import type { Finish } from '@/types/card';

import type { RememberedFieldKey } from '../hooks/useRememberedFields';
import CardInstanceForm, { type CardInstanceFormData } from './CardInstanceForm';
import ExistingInstancesList from './ExistingInstancesList';
import RememberedFieldsPopover from './RememberedFieldsPopover';

export interface RememberedFieldsControls {
  rememberedFields: RememberedFieldKey[];
  onToggle: (key: RememberedFieldKey) => void;
  onReset: () => void;
  /** e.g. "this folder" — where remembered values apply */
  contextLabel: string;
}

export interface AddCardDetailsStepProps {
  printing: MinimalCard;
  formData: CardInstanceFormData;
  onFormChange: (data: CardInstanceFormData) => void;
  /** Switch to a finish the printing exists in, without touching remembered values */
  onFinishUnavailable: (finish: Finish) => void;
  disabled: boolean;
  /** Omit to hide the remembered-fields UI (edit mode) */
  memory?: RememberedFieldsControls;
  /** Owned instances with +1 buttons (default true) */
  showExistingInstances?: boolean;
  /** Form heading; defaults to "Add New Instance" */
  formTitle?: string;
  onIncremented?: () => void;
}

export default function AddCardDetailsStep({
  printing,
  formData,
  onFormChange,
  onFinishUnavailable,
  disabled,
  memory,
  showExistingInstances = true,
  formTitle,
  onIncremented,
}: AddCardDetailsStepProps): ReactElement {
  const { data: collection } = useCollectionQuery();
  const { getFullCard, prefetchCard } = useCardLookup();
  const [displayFaceIndex, setDisplayFaceIndex] = useState(0);
  const formRef = useRef<HTMLDivElement>(null);

  // The clicked edition tile unmounts, so move focus into the form (quantity input)
  useEffect(() => {
    formRef.current?.querySelector('input')?.focus({ preventScroll: true });
  }, []);

  const fullCard = getFullCard(printing.id);
  useEffect(() => {
    if (!fullCard) prefetchCard(printing.id);
  }, [printing.id, fullCard, prefetchCard]);

  const availableFinishes = getPrintingFinishes(fullCard);
  const isFinishUnavailable = availableFinishes !== null && !availableFinishes.includes(formData.finish);
  useEffect(() => {
    if (isFinishUnavailable) onFinishUnavailable(availableFinishes[0]);
  }, [isFinishUnavailable]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDFC = Boolean(
    fullCard?.card_faces && fullCard.card_faces.length >= 2 && fullCard.card_faces.every((face) => face.image_uris?.normal),
  );
  const imageUris = (isDFC && fullCard?.card_faces?.[displayFaceIndex]?.image_uris) || printing.image_uris;
  const hasExistingInstances = showExistingInstances && (collection?.some((c) => c.scryfall_id === printing.id) ?? false);
  const rememberedCount = memory?.rememberedFields.length ?? 0;

  return (
    <div className="flex gap-6">
      {/* Card image + remembered fields */}
      <div className="flex shrink-0 flex-col items-center">
        <CardImage key={`${printing.id}-${displayFaceIndex}`} imageUris={imageUris} name={printing.name} size="large" />
        {isDFC && (
          <button
            type="button"
            onClick={() => setDisplayFaceIndex((prev) => (prev === 0 ? 1 : 0))}
            className="text-silver hover:text-pearl hover:bg-slate focus-ring mt-2 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors"
            aria-label="Flip card"
          >
            <TbRotate className="h-4 w-4" />
            Flip
          </button>
        )}
        {memory && (
          <div className="mt-3 flex max-w-61 items-center gap-1.5">
            {rememberedCount > 0 ? (
              <IoBookmark className="h-3.5 w-3.5 shrink-0 text-amber-400" />
            ) : (
              <IoBookmarkOutline className="text-ash h-3.5 w-3.5 shrink-0" />
            )}
            <p className={clsx('text-xs leading-relaxed', rememberedCount > 0 ? 'text-silver' : 'text-ash')}>
              {rememberedCount > 0
                ? `${rememberedCount} field${rememberedCount !== 1 ? 's' : ''} remembered for ${memory.contextLabel}`
                : 'No fields remembered'}
            </p>
            <RememberedFieldsPopover rememberedFields={memory.rememberedFields} onToggle={memory.onToggle} onReset={memory.onReset} />
          </div>
        )}
      </div>

      {/* Owned instances + form */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {showExistingInstances && <ExistingInstancesList scryfallId={printing.id} onIncremented={onIncremented} />}
        <div ref={formRef} className="bg-slate/30 border-storm rounded-xl border p-4">
          <h4 className="text-ash mb-3 text-xs font-medium tracking-widest uppercase">
            {formTitle ?? (hasExistingInstances ? 'Or Add New Instance' : 'Add New Instance')}
          </h4>
          <CardInstanceForm
            values={formData}
            onChange={onFormChange}
            disabled={disabled}
            idPrefix="add-"
            rememberedFields={memory?.rememberedFields}
            availableFinishes={availableFinishes}
          />
        </div>
      </div>
    </div>
  );
}
